import { useState, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { generateRoomId, generateRoomLink } from '@/lib/livekit/tokenService'
import {
  AnimatorApplication,
  AnimatorApplicationRow,
  CreateAnimatorApplicationInput,
  applicationRowToApplication,
  ANIMATOR_TYPE_LABELS,
} from '@/types/animatorApplication'
import { CalendarEventRow, CalendarEventAttendeeRow } from '@/types/calendar'

// ============================================================================
// useCreateAnimatorApplication - Submit application and create interview events
// ============================================================================

interface TeamMember {
  id: string
  email: string
  firstName?: string
  lastName?: string
}

export function useCreateAnimatorApplication() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createApplication = useCallback(async (
    input: CreateAnimatorApplicationInput,
    teamMembers: TeamMember[]
  ): Promise<AnimatorApplication | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Generate meeting room
      const meetingRoomId = generateRoomId()
      const meetingRoomLink = generateRoomLink(meetingRoomId)

      // Call the database function which has SECURITY DEFINER to bypass RLS
      const { data, error: rpcError } = await (client as any).rpc('submit_animator_application', {
        p_first_name: input.firstName,
        p_last_name: input.lastName,
        p_email: input.email,
        p_portfolio_links: input.portfolioLinks,
        p_about: input.about || null,
        p_strong_areas: input.strongAreas || null,
        p_animator_types: input.animatorTypes,
        p_scheduled_start: input.scheduledStart,
        p_scheduled_end: input.scheduledEnd,
        p_meeting_room_id: meetingRoomId,
        p_meeting_room_link: meetingRoomLink,
        p_interviewer_ids: teamMembers.map(m => m.id),
      })

      if (rpcError) throw rpcError
      if (!data) throw new Error('Failed to create application')

      // Send confirmation emails via edge function
      // Don't fail the submission if email fails
      try {
        const supabaseUrl = import.meta.env.VITE_NEXUS_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_NEXUS_SUPABASE_ANON_KEY
        if (supabaseUrl && supabaseAnonKey) {
          const applicantName = `${input.firstName} ${input.lastName}`
          const interviewerEmails = teamMembers.map(m => m.email).filter(Boolean)

          const response = await fetch(`${supabaseUrl}/functions/v1/application-confirmation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'apikey': supabaseAnonKey,
            },
            body: JSON.stringify({
              applicantEmail: input.email,
              applicantName,
              interviewDate: input.scheduledStart,
              interviewTimezone: input.timezone,
              meetingLink: data.meeting_room_link,
              interviewerEmails,
            }),
          })

          if (response.ok) {
            console.log('Confirmation emails sent successfully')
          } else {
            const errorData = await response.json().catch(() => ({}))
            console.error('Email API error:', response.status, errorData)
          }
        }
      } catch (emailErr) {
        // Log but don't fail the submission
        console.error('Failed to send confirmation emails:', emailErr)
      }

      // Return a simplified application object from the RPC response
      return {
        id: data.id,
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        portfolioLinks: input.portfolioLinks,
        about: input.about,
        strongAreas: input.strongAreas,
        animatorTypes: input.animatorTypes,
        scheduledStart: data.scheduled_start,
        scheduledEnd: data.scheduled_end,
        meetingRoomId: meetingRoomId,
        meetingRoomLink: data.meeting_room_link,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    } catch (err: any) {
      console.error('Error creating animator application:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    createApplication,
    isLoading,
    error,
  }
}

// ============================================================================
// Helper: Create interview event for one team member
// ============================================================================

async function createInterviewEvent(
  member: TeamMember,
  input: CreateAnimatorApplicationInput,
  meetingRoomId: string,
  meetingRoomLink: string,
  applicationId: string
) {
  // Use authenticated client for creating calendar events
  // This requires a service role key or admin authentication
  // For now, we'll use anon but the event will need to be created differently

  // Actually, we need to use the getNexusClient for authenticated operations
  // But since this is a public form, we need RLS to allow anon insert on calendar_events
  //
  // Alternative approach: Insert via edge function or use service role
  // For now, let's try with the existing RLS setup

  const client = getNexusClient()

  // Format applicant name
  const applicantName = `${input.firstName} ${input.lastName}`

  // Format animator types for title
  const typesList = input.animatorTypes
    .map(t => ANIMATOR_TYPE_LABELS[t])
    .slice(0, 2)
    .join(', ')
  const typesMore = input.animatorTypes.length > 2 ? ` +${input.animatorTypes.length - 2}` : ''

  const eventData: Partial<CalendarEventRow> = {
    owner_id: member.id,
    title: `Interview: ${applicantName}`,
    description: `Animator application interview

Applicant: ${applicantName}
Email: ${input.email}
Types: ${input.animatorTypes.map(t => ANIMATOR_TYPE_LABELS[t]).join(', ')}
Strong Areas: ${input.strongAreas || 'Not specified'}

Portfolio:
${input.portfolioLinks.map(link => `- ${link}`).join('\n') || 'No links provided'}

About:
${input.about || 'Not provided'}

Application ID: ${applicationId}`,
    location: meetingRoomLink,
    start_time: input.scheduledStart,
    end_time: input.scheduledEnd,
    all_day: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    has_video_conference: true,
    meeting_room_id: meetingRoomId,
    meeting_room_link: meetingRoomLink,
    event_type: 'meeting',
    color: 'orange', // Orange for interview events
    is_private: false,
  }

  const { data: eventRow, error: eventError } = await (client as any)
    .from('calendar_events')
    .insert(eventData)
    .select()
    .single() as { data: CalendarEventRow | null; error: any }

  if (eventError) {
    console.error(`Error creating event for ${member.email}:`, eventError)
    throw eventError
  }

  if (!eventRow) return

  // Add organizer (the interviewer) as attendee
  await (client as any)
    .from('calendar_event_attendees')
    .insert({
      event_id: eventRow.id,
      admin_user_id: member.id,
      response_status: 'accepted',
      is_organizer: true,
      is_optional: false,
    })

  // Add applicant as external attendee
  await (client as any)
    .from('calendar_event_attendees')
    .insert({
      event_id: eventRow.id,
      admin_user_id: null,
      external_email: input.email,
      external_name: applicantName,
      response_status: 'pending',
      is_organizer: false,
      is_optional: false,
    })
}

// ============================================================================
// useAnimatorApplications - Fetch applications (admin only)
// ============================================================================

export function useAnimatorApplications() {
  const [applications, setApplications] = useState<AnimatorApplication[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchApplications = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: fetchError } = await client
        .from('animator_applications')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setApplications((data || []).map((row: AnimatorApplicationRow) =>
        applicationRowToApplication(row)
      ))
    } catch (err: any) {
      console.error('Error fetching animator applications:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    applications,
    isLoading,
    error,
    refetch: fetchApplications,
  }
}

// ============================================================================
// useUpdateApplicationStatus - Update application status (admin only)
// ============================================================================

export function useUpdateApplicationStatus() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateStatus = useCallback(async (
    applicationId: string,
    status: AnimatorApplication['status']
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: updateError } = await (client as any)
        .from('animator_applications')
        .update({ status })
        .eq('id', applicationId)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('Error updating application status:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    updateStatus,
    isLoading,
    error,
  }
}

// ============================================================================
// Fetch team members for interview config
// ============================================================================

export async function fetchTeamMembers(userIds: string[]): Promise<TeamMember[]> {
  if (userIds.length === 0) return []

  const client = getNexusClient()

  const { data, error } = await (client as any)
    .from('admin_users')
    .select('id, email, first_name, last_name')
    .in('id', userIds)

  if (error) {
    console.error('Error fetching team members:', error)
    return []
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    email: row.email,
    firstName: row.first_name ?? undefined,
    lastName: row.last_name ?? undefined,
  }))
}
